import axios from 'axios';
import type { Demand, AIRule, User, Attachment, DemandComment, Notification, Role, Permission } from './types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
});

// Adicionar token JWT automaticamente se houver
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gab_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Tratar token expirado ou inválido
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn('Token inválido ou expirado. Redirecionando para login...');
      localStorage.removeItem('gab_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ========================================================
// 🔌 INTERFACE DE MÉTODOS DE CHAMADA DA API (Produção)
// ========================================================

export const authAPI = {
  login: async (email: string, password: String): Promise<{ token: string; user: User }> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  me: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

export const demandsAPI = {
  list: async (filters: any = {}): Promise<{ data: Demand[]; pagination: any }> => {
    const finalFilters = { limit: 5000, ...filters };
    const response = await api.get('/demands', { params: finalFilters });
    return response.data;
  },
  getById: async (id: string): Promise<Demand> => {
    const response = await api.get(`/demands/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<Demand> => {
    const response = await api.post('/demands', data);
    return response.data;
  },
  update: async (id: string, data: any): Promise<{ success: boolean; demand: Demand; fieldsChanged?: any[] }> => {
    const response = await api.put(`/demands/${id}`, data);
    return response.data;
  },
  updateStatus: async (id: string, status: string, notes?: string): Promise<boolean> => {
    await api.patch(`/demands/${id}/status`, { status, notes });
    return true;
  },
  approveAndNotify: async (id: string, data: { sendWhatsApp: boolean; sendEmail: boolean; messageText: string; whatsappNumber?: string; emailAddress?: string; whatsappNumbers?: string[]; emailAddresses?: string[]; pdfBase64?: string; templateCode?: string; variables?: any; authorityId?: string; templateId?: string; editedHtml?: string }): Promise<any> => {
    const response = await api.post(`/demands/${id}/approve`, data);
    return response.data;
  },
  resendDocument: async (id: string, documentId: string, data: { sendWhatsApp: boolean; sendEmail: boolean; whatsappNumbers?: string[]; emailAddresses?: string[] }): Promise<any> => {
    const response = await api.post(`/demands/${id}/documents/${documentId}/resend`, data);
    return response.data;
  },
  deliverDocument: async (id: string, documentId: string, data: { sendWhatsApp: boolean; sendEmail: boolean; whatsappNumbers?: string[]; emailAddresses?: string[] }): Promise<any> => {
    const response = await api.post(`/demands/${id}/documents/${documentId}/deliver`, data);
    return response.data;
  },
  getTemplates: async (): Promise<any[]> => {
    const response = await api.get('/document-templates');
    return response.data;
  },
  createTemplate: async (data: any): Promise<any> => {
    const response = await api.post('/document-templates', data);
    return response.data;
  },
  updateTemplate: async (id: string, data: any): Promise<any> => {
    const response = await api.put(`/document-templates/${id}`, data);
    return response.data;
  },
  updateTemplateStatus: async (id: string, status: string): Promise<any> => {
    const response = await api.patch(`/document-templates/${id}/status`, { status });
    return response.data;
  },
  uploadTemplateSource: async (id: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/document-templates/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  duplicateTemplate: async (id: string, data: { target: 'VERSION' | 'NEW_CODE'; newCode?: string; name?: string }): Promise<any> => {
    const response = await api.post(`/document-templates/${id}/duplicate`, data);
    return response.data;
  },
  extractTemplateContent: async (id: string): Promise<any> => {
    const response = await api.get(`/document-templates/${id}/extract`);
    return response.data;
  },
  getAuthorities: async (): Promise<any[]> => {
    const response = await api.get('/authorities');
    return response.data;
  },
  updateNotes: async (id: string, internalNotes: string): Promise<boolean> => {
    await api.patch(`/demands/${id}/notes`, { internalNotes });
    return true;
  },
  reprocess: async (id: string, mode: 'replace' | 'suggest'): Promise<{ success: boolean; demand?: Demand; suggestions?: any }> => {
    const response = await api.post(`/demands/${id}/reprocess`, { mode });
    return response.data;
  },
  addAttachment: async (id: string, attachment: { fileName: string; fileType: string; fileSize: number; fileUrl: string }): Promise<Attachment> => {
    const response = await api.post(`/demands/${id}/attachments`, attachment);
    return response.data;
  },
  uploadAttachment: async (id: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/demands/${id}/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  removeAttachment: async (id: string, attachmentId: string): Promise<boolean> => {
    await api.delete(`/demands/${id}/attachments/${attachmentId}`);
    return true;
  },
  toggleFavorite: async (id: string): Promise<{ success: boolean; demand: Demand }> => {
    // Busca a demanda atual para saber o estado atual e fazer o toggle
    const demandResponse = await api.get(`/demands/${id}`);
    const isFav = demandResponse.data ? !demandResponse.data.isFavorite : true;
    const response = await api.put(`/demands/${id}`, { isFavorite: isFav });
    return { success: true, demand: response.data };
  },
  delete: async (id: string, reason: string): Promise<boolean> => {
    await api.delete(`/demands/${id}`, { data: { reason } });
    return true;
  },
  processIA: async (title: string, description: string): Promise<any> => {
    const response = await api.post('/demands/process-text-ia', { title, description });
    const ext = response.data.extraction;
    
    let cab: 'STATE' | 'FEDERAL' | 'BOTH' = 'STATE';
    if (ext.gabinete_destinatario?.includes('Federal') || ext.gabinete_destinatario?.includes('Augusto')) {
      cab = 'FEDERAL';
    } else if (ext.gabinete_destinatario?.includes('Ambos')) {
      cab = 'BOTH';
    }

    let val: any = ext.valor_solicitado || '';
    if (typeof val === 'string') {
      let valStr = String(val).replace(/[^\d.,]/g, '');
      const lastDot = valStr.lastIndexOf('.');
      const lastComma = valStr.lastIndexOf(',');
      
      if (lastDot > -1 && lastComma > -1) {
        if (lastComma > lastDot) {
          valStr = valStr.replace(/\./g, '').replace(',', '.');
        } else {
          valStr = valStr.replace(/,/g, '');
        }
      } else if (lastComma > -1) {
        const commaCount = (valStr.match(/,/g) || []).length;
        if (commaCount > 1) {
          valStr = valStr.replace(/,/g, '');
        } else {
          const digitsAfterComma = valStr.length - 1 - lastComma;
          if (digitsAfterComma === 3) {
            valStr = valStr.replace(',', '');
          } else {
            valStr = valStr.replace(',', '.');
          }
        }
      } else if (lastDot > -1) {
        const dotCount = (valStr.match(/\./g) || []).length;
        if (dotCount > 1) {
          valStr = valStr.replace(/\./g, '');
        } else {
          const digitsAfterDot = valStr.length - 1 - lastDot;
          if (digitsAfterDot === 3) {
            valStr = valStr.replace('.', '');
          }
        }
      }
      
      val = valStr && !isNaN(Number(valStr)) ? Number(valStr) : '';
    }
    return {
      themeArea: ext.area_tematica || 'Saúde',
      city: ext.cidade || 'Marília',
      cabinet: cab,
      solicitanteName: ext.solicitante || 'Não identificado',
      summary: ext.resumo_didatico || description || title,
      valueRequested: val
    };
  },
  searchNLP: async (query: string): Promise<{ data: Demand[]; filtersApplied: any }> => {
    const response = await api.post('/demands/search-nlp', { query });
    return response.data;
  },
  processDocument: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/demands/process-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

export const rulesAPI = {
  list: async (): Promise<AIRule[]> => {
    const response = await api.get('/ai-rules');
    return response.data;
  },
  create: async (term: string, mappedArea: string, mappedAgencyId?: string): Promise<AIRule> => {
    const response = await api.post('/ai-rules', { term, mappedArea, mappedAgencyId });
    return response.data;
  },
  delete: async (id: string): Promise<boolean> => {
    await api.delete(`/ai-rules/${id}`);
    return true;
  }
};

export const messagesAPI = {
  list: async (demandId: string): Promise<any[]> => {
    const response = await api.get(`/demands/${demandId}/messages`);
    return response.data;
  }
};

export const commentsAPI = {
  list: async (demandId: string): Promise<DemandComment[]> => {
    const response = await api.get(`/demands/${demandId}/comments`);
    return response.data;
  },
  create: async (demandId: string, text: string): Promise<DemandComment> => {
    const response = await api.post(`/demands/${demandId}/comments`, { text });
    return response.data;
  }
};

export const notificationsAPI = {
  list: async (): Promise<Notification[]> => {
    const response = await api.get('/notifications');
    return response.data;
  },
  markAsRead: async (id: string): Promise<Notification> => {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  },
  markAllRead: async (): Promise<{ success: boolean }> => {
    const response = await api.patch('/notifications/read-all');
    return response.data;
  }
};

export const usersAPI = {
  list: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },
  create: async (userData: any): Promise<User> => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  update: async (id: string, userData: any): Promise<User> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  listRoles: async (): Promise<Role[]> => {
    const response = await api.get('/roles');
    return response.data;
  },
  listPermissions: async (): Promise<Permission[]> => {
    const response = await api.get('/permissions');
    return response.data;
  },
  updateRolePermissions: async (roleId: string, permissionIds: string[]): Promise<any> => {
    const response = await api.put(`/roles/${roleId}/permissions`, { permissionIds });
    return response.data;
  },
  delete: async (id: string): Promise<boolean> => {
    await api.delete(`/users/${id}`);
    return true;
  }
};

export const profileAPI = {
  update: async (data: any): Promise<any> => {
    const response = await api.put('/profile', data);
    return response.data;
  },
  updatePassword: async (data: any): Promise<any> => {
    const response = await api.put('/profile/password', data);
    return response.data;
  }
};

export const contactsAPI = {
  list: async (): Promise<any[]> => {
    const response = await api.get('/contacts');
    return response.data;
  },
  create: async (contactData: any): Promise<{ success: boolean; contact: any }> => {
    const response = await api.post(`/contacts`, contactData);
    return response.data;
  },
  update: async (id: string, contactData: any): Promise<{ success: boolean; contact: any }> => {
    const response = await api.put(`/contacts/${id}`, contactData);
    return response.data;
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  },
  importCSV: async (file: File): Promise<{ success: boolean; imported: number; skipped: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

export const inboxStatsAPI = {
  stats: async (): Promise<{
    recebidas: number;
    processando: number;
    revisaoObrigatoria: number;
    resolvidasHoje: number;
    total: number;
  }> => {
    const response = await api.get('/demands/stats');
    return response.data;
  }
};

export const attachmentsAPI = {
  fetchBlob: async (fileUrl: string): Promise<{ blob: Blob; mimeType: string } | null> => {
    try {
      const token = localStorage.getItem('gab_token');
      
      // Extrair apenas o nome do arquivo da URL (ignorando localhost, http vs https, etc)
      let finalUrl = fileUrl;
      const filenameMatch = fileUrl.match(/\/([^\/]+)$/);
      if (filenameMatch && filenameMatch[1]) {
        // Garantir que não inclui parâmetros de query perdidos na extração
        const filename = filenameMatch[1].split('?')[0];
        finalUrl = `${API_URL.replace(/\/api$/, '')}/api/attachments/${filename}`;
      }

      const response = await fetch(finalUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return { blob, mimeType: blob.type || 'application/octet-stream' };
    } catch (e) {
      console.warn('[Attachments] Falha ao buscar arquivo:', e);
      return null;
    }
  }
};

export const manualDemandAPI = {
  createViaText: async (text: string): Promise<{ success: boolean; jobId: string; messageId: string }> => {
    const webhookUrl = `${API_URL}/integrations/whatsapp-webhook`;
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: '00000000000', messageText: `[CADASTRO MANUAL] ${text}` }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Erro ao criar demanda' }));
      throw new Error(err.error || 'Falha ao criar demanda');
    }
    return response.json();
  }
};

export const auditAPI = {
  list: async (params?: any): Promise<any[]> => {
    const response = await api.get('/audit', { params });
    return response.data;
  }
};

export const crmAPI = {
  // Municipalities
  listMunicipalities: async (): Promise<any[]> => {
    const res = await api.get('/crm/municipalities');
    return res.data;
  },
  createMunicipality: async (data: any): Promise<any> => {
    const res = await api.post('/crm/municipalities', data);
    return res.data;
  },
  updateMunicipality: async (id: string, data: any): Promise<any> => {
    const res = await api.put(`/crm/municipalities/${id}`, data);
    return res.data;
  },
  deleteMunicipality: async (id: string): Promise<any> => {
    const res = await api.delete(`/crm/municipalities/${id}`);
    return res.data;
  },
  importMunicipalitiesCSV: async (file: File): Promise<{ success: boolean; imported: number; updated: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/crm/municipalities/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  },

  // People
  listPeople: async (): Promise<any[]> => {
    const res = await api.get('/crm/people');
    return res.data;
  },
  createPerson: async (data: any): Promise<any> => {
    const res = await api.post('/crm/people', data);
    return res.data;
  },
  updatePerson: async (id: string, data: any): Promise<any> => {
    const res = await api.put(`/crm/people/${id}`, data);
    return res.data;
  },
  deletePerson: async (id: string): Promise<any> => {
    const res = await api.delete(`/crm/people/${id}`);
    return res.data;
  },
  importPeopleCSV: async (file: File): Promise<{ success: boolean; imported: number; updated: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/crm/people/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  },

  // Entities
  listEntities: async (): Promise<any[]> => {
    const res = await api.get('/crm/entities');
    return res.data;
  },
  createEntity: async (data: any): Promise<any> => {
    const res = await api.post('/crm/entities', data);
    return res.data;
  },
  updateEntity: async (id: string, data: any): Promise<any> => {
    const res = await api.put(`/crm/entities/${id}`, data);
    return res.data;
  },
  deleteEntity: async (id: string): Promise<any> => {
    const res = await api.delete(`/crm/entities/${id}`);
    return res.data;
  },

  // Resource Objects
  listResourceObjects: async (): Promise<any[]> => {
    const res = await api.get('/crm/resource-objects');
    return res.data;
  },
  createResourceObject: async (data: any): Promise<any> => {
    const res = await api.post('/crm/resource-objects', data);
    return res.data;
  },
  updateResourceObject: async (id: string, data: any): Promise<any> => {
    const res = await api.put(`/crm/resource-objects/${id}`, data);
    return res.data;
  },
  deleteResourceObject: async (id: string): Promise<any> => {
    const res = await api.delete(`/crm/resource-objects/${id}`);
    return res.data;
  },

  // Allocations
  listAllocations: async (): Promise<any[]> => {
    const res = await api.get('/crm/allocations');
    return res.data;
  },
  createAllocation: async (data: any): Promise<any> => {
    const res = await api.post('/crm/allocations', data);
    return res.data;
  },
  updateAllocation: async (id: string, data: any): Promise<any> => {
    const res = await api.put(`/crm/allocations/${id}`, data);
    return res.data;
  },
  deleteAllocation: async (id: string): Promise<any> => {
    const res = await api.delete(`/crm/allocations/${id}`);
    return res.data;
  },

  // Inteligência, Relacionamentos e Aliases (Fase 5)
  getMunicipalityIntelligence: async (id: string): Promise<any> => {
    const res = await api.get(`/crm/municipalities/${id}/intelligence`);
    return res.data;
  },
  resolveRelationship: async (demandId: string, data: { action: 'CONFIRMED' | 'REJECTED' | 'CHANGED' | 'IGNORED'; municipalityId?: string; entityId?: string; personId?: string; allocationId?: string; reason?: string }): Promise<any> => {
    const res = await api.post(`/crm/demands/${demandId}/resolve-relationship`, data);
    return res.data;
  },
  listAliases: async (entityId?: string): Promise<any[]> => {
    const res = await api.get('/crm/entities/aliases', { params: { entityId } });
    return res.data;
  },
  createAlias: async (entityId: string, alias: string): Promise<any> => {
    const res = await api.post('/crm/entities/aliases', { entityId, alias });
    return res.data;
  }
};

